import { useI18n } from "@/lib/i18n";
import {
  ShoppingBag, Plane, Hotel, Hospital, Briefcase, Building2, LayoutGrid, Home,
  Landmark, Car, KeyRound, Camera,
} from "lucide-react";

const ICONS = [
  ShoppingBag, Plane, Hotel, Hospital, Briefcase, Building2, LayoutGrid, Home,
  Landmark, Car, KeyRound, Camera,
];

const MALL_IMG = "https://images.unsplash.com/photo-1566454869316-b6cfb093a071?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHw0fHxkdWJhaSUyMG1hbGwlMjBtb2Rlcm4lMjBhcmNoaXRlY3R1cmUlMjBpbnRlcmlvcnxlbnwwfHx8fDE3ODM5NjM2NDh8MA&ixlib=rb-4.1.0&q=85";
const CORP_IMG = "https://images.unsplash.com/photo-1758599543117-f996daf4978b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHw0fHxidXNpbmVzc21hbiUyMHVzaW5nJTIwc21hcnRwaG9uZSUyMG1vZGVybiUyMGdsYXNzJTIwYnVpbGRpbmd8ZW58MHx8fHwxNzgzOTYzNjQ5fDA&ixlib=rb-4.1.0&q=85";

export default function DesignedForUAE() {
  const { t } = useI18n();
  const cases = t("uae.cases");

  return (
    <section id="solutions" className="section bg-white" data-testid="section-uae">
      <div className="container-x">
        <div className="max-w-3xl">
          <div className="eyebrow">{t("uae.eyebrow")}</div>
          <h2 className="h2 mt-4">{t("uae.title")}</h2>
          <p className="lede mt-5">{t("uae.lede")}</p>
        </div>

        {/* Bento */}
        <div className="mt-14 grid grid-cols-1 md:grid-cols-12 gap-5">
          <div className="md:col-span-7 rounded-3xl overflow-hidden border border-brand-line relative min-h-[280px] group">
            <img src={MALL_IMG} alt="Dubai mall modern architecture interior" loading="lazy"
              className="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/25 to-transparent" />
            <div className="absolute bottom-0 inset-x-0 p-7">
              <div className="text-xs uppercase tracking-[0.22em] text-brand-gold font-semibold">
                Dubai · Abu Dhabi · Sharjah
              </div>
              <div className="mt-2 text-white text-2xl md:text-3xl font-extrabold">
                {cases[0]} · {cases[1]} · {cases[2]}
              </div>
            </div>
          </div>
          <div className="md:col-span-5 rounded-3xl border border-brand-line bg-brand-sand p-7 flex flex-col">
            <div className="text-xs uppercase tracking-[0.22em] text-brand-green font-semibold">
              12+ {t("uae.eyebrow")}
            </div>
            <div className="mt-3 text-brand-charcoal text-2xl md:text-3xl font-extrabold leading-tight">
              {cases[3]} · {cases[4]} · {cases[5]}
            </div>
            <div className="mt-6 grid grid-cols-2 gap-3">
              {cases.slice(6, 12).map((c, i) => {
                const Icon = ICONS[i + 6] || ShoppingBag;
                return (
                  <div key={i} className="flex items-center gap-2 rounded-xl bg-white border border-brand-line p-3">
                    <span className="h-7 w-7 rounded-lg bg-brand-greenSoft text-brand-green flex items-center justify-center">
                      <Icon className="h-4 w-4" />
                    </span>
                    <span className="text-sm font-semibold text-brand-charcoal leading-tight">{c}</span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="md:col-span-5 rounded-3xl overflow-hidden border border-brand-line relative min-h-[260px] group">
            <img src={CORP_IMG} alt="Businessman using smartphone outside modern building" loading="lazy"
              className="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
            <div className="absolute inset-0 bg-gradient-to-t from-brand-charcoal/70 via-brand-charcoal/20 to-transparent" />
            <div className="absolute bottom-0 inset-x-0 p-7">
              <div className="text-xs uppercase tracking-[0.22em] text-brand-gold font-semibold">
                {cases[4]}
              </div>
              <div className="mt-2 text-white text-2xl font-extrabold">
                {cases[5]}
              </div>
            </div>
          </div>

          <div className="md:col-span-7 rounded-3xl border border-brand-line bg-brand-charcoal text-white p-7 md:p-9 relative overflow-hidden">
            <div className="absolute -top-10 -end-10 h-40 w-40 rounded-full bg-brand-gold/20 blur-2xl" />
            <div className="text-xs uppercase tracking-[0.22em] text-brand-gold font-semibold">
              UAE-first
            </div>
            <div className="mt-3 text-2xl md:text-3xl font-extrabold leading-tight max-w-lg">
              {t("uae.title")}
            </div>
            <div className="mt-6 flex flex-wrap gap-2">
              {cases.map((c, i) => (
                <span key={i} className="text-xs px-3 py-1.5 rounded-full bg-white/10 border border-white/15 font-medium">
                  {c}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
